import { makeStyles } from "tss-react/mui";
import { BUTTON_VARIANTS, INSPECTION_STATUSES } from "@miplatform/constants";
import {
  IInspectionAttempt,
  IMiListField,
  InspectionActionTypes,
} from "@miplatform/types";
import { getDefaultDate, getInspectionMenuItems } from "@miplatform/utils";
import { mdiInformationSlabCircleOutline, mdiTag } from "@mdi/js";
import { Box, Tooltip, Typography } from "@mui/material";
import { MiButton } from "../../MiButton/MiButton";
import { MiIcon } from "../../MiIcon/MiIcon";
import { MiInspectionPopper } from "../../MiInspectionCard/MiInspectionPopper";
import { MiWorkListCard } from "../MiWorkListCard";
import { InspectionCardStatus } from "./InspectionWorkListCardStatus";

export const InspectionWorkListCard = ({
  item,
  onMenuItemClick,
}: InspectionWorkListCardProps) => {
  const { classes, theme } = useStyles();

  const fields: IMiListField<IInspectionAttempt>[] = [
    {
      name: "attemptNumber",
      label: "Attempt Number",
      field: "attemptNumber",
      renderValue: (value: IInspectionAttempt) => `#${value.attemptNumber}`,
    },
    {
      label: "Scheduled Date",
      field: "approvedStartDate",
      name: "approvedStartDate",
      renderValue: (value: IInspectionAttempt) =>
        getDefaultDate(value.approvedStartDate),
    },
    {
      label: "Order Number",
      field: "orderNumber",
      name: "orderNumber",
    },
  ];

  const renderTitle = (inspection: IInspectionAttempt) => {
    const { attemptStatus, templateName, hasBeenFailed } = inspection;
    const isGreenTag = attemptStatus === INSPECTION_STATUSES.PASSED;
    const isRedTag =
      attemptStatus === INSPECTION_STATUSES.FAILED || hasBeenFailed;
    const renderTag = isGreenTag || isRedTag;

    return (
      <Box className={classes.inspectionWorkListCardTitle}>
        <Typography variant="HD2" color={theme.palette.primary.main}>
          {templateName}
        </Typography>
        <Tooltip
          title={<MiInspectionPopper inspection={inspection} />}
          slotProps={{
            popper: {
              placement: "right",
              modifiers: [
                {
                  name: "offset",
                  options: { offset: [0, -18] },
                },
              ],
              sx: { p: 0, m: 0 },
            },
            tooltip: { sx: { p: 0, m: 0 } },
          }}
        >
          <Box>
            <MiButton
              size="small"
              variant={BUTTON_VARIANTS.CONTAINED}
              color="secondary"
              startIcon={{
                path: mdiInformationSlabCircleOutline,
                iconSize: 0.5,
              }}
              style={{ padding: theme.spacing(0, 1) }}
            />
          </Box>
        </Tooltip>
        {renderTag && (
          <Box
            sx={{
              backgroundColor: isGreenTag
                ? theme.palette.success.main
                : theme.palette.error.main,
            }}
            className={classes.statusTag}
          >
            <MiIcon
              path={mdiTag}
              size={0.4}
              color={theme.palette.common.white}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <MiWorkListCard
      item={item}
      showNulls
      LeftComponent={InspectionCardStatus}
      renderTitle={renderTitle}
      getMenuItems={(item: IInspectionAttempt) =>
        getInspectionMenuItems({
          data: item,
          onMenuItemClick,
          theme,
        })
      }
      fieldset={fields}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  cardContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
  },
  inspectionWorkListCardTitle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    height: "100%",
  },
  statusTag: {
    padding: theme.spacing(0, 1),
    borderRadius: 4,
    height: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

interface InspectionWorkListCardProps {
  item: IInspectionAttempt;
  onMenuItemClick: ({
    inspectionAttempt,
    actionType,
  }: {
    inspectionAttempt: IInspectionAttempt;
    actionType: InspectionActionTypes | null;
  }) => void;
}
